import axios from "axios";
import apiUrlConfig from "../../environmentSettings";

// indicator toggle is visible in the Configure panel.
const INDICATOR_ENABLED_WIDGETS = new Set([
	"DRAG_LINE_CHART_FUSION",
	"DUAL_AXIS_STACKED_COLUMN_LINE_FUSION",
	"NESTED_PIE_CHART_FUSION",
	"MS_STACKED_COLUMN_CHART_FUSION",
	"COLUMN_CHART_3D_FUSION",
	"BAR_CHART_3D_FUSION",
	"PIE_CHART_3D_FUSION",
	"DONUT_CHART_3D_FUSION",
	"MS_SPLINE_CHART_FUSION",
	"STACKED_COLUMNS_AND_LINE"
]);

const normalizeDatasource = (datasource, widgetId) => {
	// For targeted widgets, force the editor to show only Indicator (plus Manual toggle)
	if (INDICATOR_ENABLED_WIDGETS.has(widgetId)) {
		return "INDICATOR";
	}

	// Fallback to existing datasource or default to INDICATOR
	return datasource || "INDICATOR";
};

export const getWidgetsConfig = async (accessToken = null) => {
	try {
		const WIDGET_CONFIG_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/refWidgets?access_token=${accessToken ||
			localStorage.$LoopBack$accessTokenId}`;
		
		const response = await fetch(WIDGET_CONFIG_URL);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		
		const widgets = await response.json();
		const widgetList = widgets.map(widget => {
			const datasource = normalizeDatasource(widget.datasource, widget.id);
			return {
				name: widget.name,
				picassoComponentId: widget.picassoComponentId,
				refComponentId: widget.refComponentId,
				icon: widget.icon,
				configureWidgetProps: widget.configureWidgetProps || {
					height: 400,
					width: 550
				},
				datasource,
				defaultSettings: {
					data: widget.fact,
					propsObject: widget.props,
					config: widget.config
				},
				id: widget.id,
				dataConfigProps: widget.dataGeneratorConfig,
				type: widget.type,
				sortOrder: widget.sortOrder
			};
		});

		const widgetResult = widgetList.reduce((result, widget) => {
			if (result.widgetHash) {
				result.widgetHash[widget.id] = widget;
			} else {
				result.widgetHash = {
					[widget.id]: widget
				};
			}
			if (result.widgetComponent) {
				if (result.widgetComponent[widget.refComponentId]) {
					result.widgetComponent[widget.refComponentId].push(widget.id);
				} else {
					result.widgetComponent[widget.refComponentId] = [widget.id];
				}
			} else {
				result.widgetComponent = {
					[widget.refComponentId]: [widget.id]
				};
			}
			return result;
		}, {});

		return widgetResult;
	} catch (error) {
		console.error("Error fetching widget config:", error);
		return Promise.reject(error);
	}
};

export const getWidgets = () => {
	const WIDGET_CONFIG_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/refWidgets?filter={"where": {"deprecated": false}}&access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(WIDGET_CONFIG_URL)
		.then(response => response.json())
		.then(widgets => {
			return widgets;
		});
};

export const getComponents = () => {
	const COMPONENT_CONFIG_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/refComponents?access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(COMPONENT_CONFIG_URL)
		.then(response => response.json())
		.then(components => {
			return components.map(component => {
				return { ...component, widgets: [] };
			});
		});
};

export const getIcons = () => {
	const ICON_CONFIG_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/refIcons?access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(ICON_CONFIG_URL)
		.then(response => response.json())
		.then(icons => {
			return icons;
		});
};

export const getIconsCategory = () => {
	const CATEGORY_CONFIG_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/refAspects?access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(CATEGORY_CONFIG_URL)
		.then(response => response.json())
		.then(categories => {
			return categories.map(category => {
				return { ...category, icons: [] };
			});
		});
};

export const getIndicatorsInOrg = (organizationId, accessToken) => {
	return axios
		.get(`${apiUrlConfig.apiConfig.p3fyUrl}/indicators`, {
			headers: { Authorization: accessToken },
			params: {
				filter: {
					where: {
						organizationId
					}
				}
			}
		})
		.then(function(response) {
			return response.data;
		});
};

export const getQueriesInOrg = organizationId => {
	return axios
		.get(`/api/queries`, {
			params: {
				filter: {
					where: {
						organizationId: parseInt(organizationId, 10)
					}
				}
			}
		})
		.then(response => response.data)
		.then(queries => {
			return queries.filter(query => query.title !== undefined);
		});
};

export const getSprinkleQueriesInOrg = organizationId => {
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios
		.post(
			`/api/indicators/getSprinkleQueries`,
			{
				organizationId
			},
			config
		)
		.then(response => response.data)
		.then(queries => {
			console.log(queries);
			return queries;
		});
};

export const getOrgRole = orgId => {
	const filterBody = { where: { userId: localStorage.$LoopBack$currentUserId, organizationId: orgId } };
	const ROLE_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/organizationUsers?access_token=${
		localStorage.$LoopBack$accessTokenId
	}&filter=${JSON.stringify(filterBody)}`;
	return fetch(ROLE_URL)
		.then(response => response.json())
		.then(orgUser => {
			return orgUser && orgUser[0] ? orgUser[0].role : "";
		});
};

export const uploadFile = formData => {
	const apiConfigObj = apiUrlConfig.apiConfig;
	const url = `${apiConfigObj.p3fyUrl}/containers/${apiConfigObj.assetUploadBucket}`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "multipart/form-data"
		}
	};

	return axios.post(`${url}/upload`, formData, config).then(response => ({
		...response.data,
		src: `${url}/download/${response.data.key || response.data.Key}`
	}));
};

export const getOrganizationUsers = organizationId => {
	const ALLOWED_ROLES = ["ORGMONITOR", "GRANTADMIN", "GRANTNGO", "EMPLOYEE"];
	const USER_FETCH_URL = `${
		apiUrlConfig.apiConfig.p3fyUrl
	}/organizationUsers?filter={"where": {"role": {"inq": ${JSON.stringify(
		ALLOWED_ROLES
	)}}, "organizationId": ${organizationId}}, "include": "user","fields":["id","userId","managerId","organizationId","organizationOfficeId","isPrimary","role","acceptedEmpTerms","securityContextId","created","creatorId","modified","modifierId","source","isAPIUser"]}&access_token=${
		localStorage.$LoopBack$accessTokenId
	}`;
	return fetch(USER_FETCH_URL).then(response => response.json());
};

export const getRoles = () => {
	const apiConfigObj = apiUrlConfig.apiConfig;
	const url = `${apiConfigObj.p3fyUrl}/users/getRoles`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	const payload = { context: "global", contextId: null };
	return axios.post(url, payload, config).then(response => {
		return response.data.roles;
	});
};

export const duplicate = (id, model, extraProps) => {
	const body = {
		id,
		...extraProps,
		model,
		securityContextId: "_id"
	};
	const url = `${apiUrlConfig.apiConfig.duplicateServiceUrl}/duplicate/triggerDuplication`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios.post(url, body, config).then(response => response);
};

export const getTemporalities = params => {
	return axios
		.get(`${apiUrlConfig.apiConfig.p3fyUrl}/temporalities/getTimePeriods`, {
			headers: {
				Authorization: localStorage.$LoopBack$accessTokenId,
				"Content-Type": "application/json"
			},
			params: {
				params
			}
		})
		.then(function(response) {
			return response;
		});
};

export const draxDownloadData = (params, zipDownload) => {
	const body = params.dataConfig[0].hasOwnProperty("indicatorId")
		? {
				...params,
				organizationId: params.orgId,
				userId: localStorage.$LoopBack$currentUserId
		  }
		: {
				...params.dataConfig[0],
				filename: params.filename ? params.filename.replace(/ /g, "_") : "",
				organizationId: params.orgId,
				userId: localStorage.$LoopBack$currentUserId
		  };

	const actionName = zipDownload
		? "getZip"
		: params.dataConfig[0].hasOwnProperty("indicatorId")
		? "getIndicatorsData"
		: "getFactsData";

	const url = `${apiUrlConfig.apiConfig.draxUrl}/canvasDownload/${params.actionId ?? 1}/${actionName}`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios.post(url, body, config).then(response => response);
};

export const registerWebToken = token => {
	const body = {
		token
	};
	const url = `${apiUrlConfig.apiConfig.p3fyUrl}/users/registerWebToken`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios.post(url, body, config).then(() => {});
};

export const scheduleDashboardDownload = (schedulerObj, reportSchedulerObj) => {
	const url = `${apiUrlConfig.apiConfig.p3fyUrl}/schedulers`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios.post(url, schedulerObj, config).then(response => {
		const body = {
			...reportSchedulerObj,
			schedulerId: parseInt(response.data.id, 10)
		};
		return axios.post("/api/reportSchedulers", body);
	});
};

export const getReportSchedulersInOrg = organizationId => {
	return axios
		.get(`/api/reportSchedulers`, {
			params: {
				filter: {
					where: {
						organizationId: parseInt(organizationId, 10),
						active: true
					}
				}
			}
		})
		.then(response => response.data)
		.then(reports => {
			return reports;
		});
};

export const updateReportScheduler = (schedulerId, reportId, schedulerObj, reportSchedulerObj) => {
	const deleteUrl = `${apiUrlConfig.apiConfig.p3fyUrl}/schedulers/${schedulerId}`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	return axios.delete(deleteUrl, {}, config).then(response => {
		const postUrl = `${apiUrlConfig.apiConfig.p3fyUrl}/schedulers`;
		return axios.post(postUrl, schedulerObj, config).then(res => {
			const body = {
				...reportSchedulerObj,
				schedulerId: parseInt(res.data.id, 10)
			};
			return axios.put(`/api/reportSchedulers/${reportId}`, body).then(reports => {});
		});
	});
};

export const deleteReports = (report, schedulerId) => {
	const url = `${apiUrlConfig.apiConfig.p3fyUrl}/schedulers/${schedulerId}`;
	const config = {
		headers: {
			Authorization: localStorage.$LoopBack$accessTokenId,
			"Content-Type": "application/json"
		}
	};
	const reports = {
		...report,
		active: false
	};
	delete reports["_id"];

	return axios.delete(url, {}, config).then(response => {
		return axios.put(`/api/reportSchedulers/${report.id}`, reports).then(() => {});
	});
};

export const getAllOrganizationUsers = organizationId => {
	const ALLOWED_ROLES = [
		"ORGMONITOR",
		"GRANTADMIN",
		"GRANTNGO",
		"EMPLOYEE",
		"ORGADMIN",
		"MANAGER",
		"GRANTMANAGER",
		"UNIQUSDMIN",
		"CANVASADMIN",
		"CONTRACTOR",
		"NOMINATOR"
	];
	const USER_FETCH_URL = `${
		apiUrlConfig.apiConfig.p3fyUrl
	}/organizationUsers?filter={"where": {"role": {"inq": ${JSON.stringify(
		ALLOWED_ROLES
	)}}, "organizationId": ${organizationId}}, "include": "user","fields":["id","userId","managerId","organizationId","organizationOfficeId","isPrimary","role","acceptedEmpTerms","securityContextId","created","creatorId","modified","modifierId","source","isAPIUser"]}&access_token=${
		localStorage.$LoopBack$accessTokenId
	}`;
	return fetch(USER_FETCH_URL).then(response => response.json());
};

export const getOrganisationTags = async programId => {
	return axios
		.get(`${apiUrlConfig.apiConfig.p3fyUrl}/programs/${programId}/tags`, {
			headers: {
				Authorization: localStorage.$LoopBack$accessTokenId,
				"Content-Type": "application/json"
			}
		})
		.then(response => response?.data);
};

export const getOrganisationTagElements = async (tagId, programId) => {
	return axios
		.get(
			`${apiUrlConfig.apiConfig.p3fyUrl}/tagElements?filter={"where":{"tagId":${tagId},"securityContextId":${programId}}}`,
			{
				headers: {
					Authorization: localStorage.$LoopBack$accessTokenId,
					"Content-Type": "application/json"
				}
			}
		)
		.then(response => response?.data);
};
